import { useCallback, useMemo, useState } from 'react';
import { every, uniqueId } from 'lodash';
import { ContactMethod } from '@repo/types';
import { dateUtil } from '@xtnd/web/shared/utils';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  DatePicker,
  FileUpload,
  Icon,
  Select,
  Textarea,
  showToast,
} from '@repo/ui';
import {
  ADMINISTRATIVE_ACTION_OPTIONS,
  CONTACT_METHOD_OPTIONS,
  CONTACT_RESULT_OPTIONS,
  PAYMENT_OUTCOME_OPTIONS,
} from '../../constants';
import { useAddMatterAttemptLog, useMatterInvoicesQuery } from '../../queries';
import { uploadAttemptAttachmentFile } from '../../queries/attemptAttachmentsQuery';
import { AttemptInvoice, AttemptInvoiceDetail, MatterAttempt } from '../../types';
import { useMatterARContext } from '../common';

type FormValues = Omit<MatterAttempt, 'sk'>;

type Props = {
  matterId: string | null;
  clientId: string | null;
  onCancel: () => void;
};

interface FormErrors {
  notesGlobal?: string;
  contactMethod?: string;
  attemptDateTime?: string;
  invoiceId?: string;
  followUp?: string;
}

const CollectionAttemptForm = ({ matterId, clientId, onCancel }: Props) => {
  const { daysPastDue, daysPastDueCap } = useMatterARContext();
  const { data } = useMatterInvoicesQuery(matterId, clientId, daysPastDue, daysPastDueCap);
  const [formValues, setFormValues] = useState<FormValues>({
    attemptDateTime: dateUtil.formatDate(new Date(), dateUtil.DateFormat.US),
    contactMethod: ContactMethod.PHONE,
    invoiceDetails: [],
  });
  const [formInvoiceValues, setFormInvoiceValues] = useState<AttemptInvoiceDetail[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  const matterInvoiceOptions = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map(inv => ({
      value: inv.invoiceId,
      label: inv.invoiceNumber,
    }));
  }, [data]);

  const updateFormValues = (patch: Partial<FormValues>) => {
    setFormValues({ ...formValues, ...patch });

    setErrors(prevErrors => {
      const updatedErrors = { ...prevErrors };
      Object.keys(patch).forEach(key => {
        if (key in updatedErrors) {
          delete updatedErrors[key as keyof FormErrors];
        }
      });
      return updatedErrors;
    });
  };

  const addAttempt = useAddMatterAttemptLog(matterId, clientId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: FormErrors = {};

    const hasAttemptDate = !!formValues.attemptDateTime;
    const hasContactMethod = !!formValues.contactMethod;
    const hasNotes = !!formValues.notesGlobal;

    if (!hasAttemptDate) newErrors.attemptDateTime = 'Please select attempt date';
    if (!hasContactMethod) newErrors.contactMethod = 'Please select attempt method';
    if (!hasNotes) newErrors.notesGlobal = 'Please enter attempt notes';

    if (formValues.nextFollowUp) {
      const toDate = new Date();
      if (new Date(formValues.nextFollowUp) < toDate) {
        newErrors.followUp = 'Please choose the future date for next follow up';
      }
    }

    if (formInvoiceValues && formInvoiceValues.length > 0) {
      const isValidAllInvoices = every(
        formInvoiceValues,
        (invoice: AttemptInvoiceDetail) => !!invoice.invoiceIds?.length
      );
      if (!isValidAllInvoices) {
        newErrors.invoiceId = 'Please select invoice related to this attempt';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const matterAttemptInvoices: AttemptInvoice[] = formInvoiceValues.flatMap(detail =>
      (detail.invoiceIds || []).map(id => ({
        invoiceId: id,
        paymentOutcome: detail.paymentOutcome,
        contactAttempt: detail.contactAttempt,
        administrativeFlag: detail.administrativeFlag,
        shortNote: detail.shortNote,
        extendedNote: detail.extendedNote,
      }))
    );

    try {
      const createResponse = await addAttempt.mutateAsync({
        ...formValues,
        invoiceDetails: matterAttemptInvoices,
        attemptDateTime: dateUtil.formatDate(
          new Date(formValues.attemptDateTime),
          dateUtil.DateFormat.ISO
        ),
        ...(formValues.nextFollowUp
          ? {
              nextFollowUp: dateUtil.formatDate(
                new Date(formValues.nextFollowUp),
                dateUtil.DateFormat.ISO
              ),
            }
          : {}),
      });

      const sk = createResponse?.data?.sk;

      if (stagedFiles.length === 0 || !sk || !matterId || !clientId) {
        showToast.success('Collection attempt logged');
        onCancel();
        return;
      }

      setIsUploadingAttachments(true);
      const failures: string[] = [];
      for (const file of stagedFiles) {
        try {
          await uploadAttemptAttachmentFile(matterId, clientId, sk, file);
        } catch {
          failures.push(file.name);
        }
      }
      setIsUploadingAttachments(false);

      const total = stagedFiles.length;
      const failed = failures.length;
      const succeeded = total - failed;

      if (failed === 0) {
        showToast.success(`Collection attempt logged. ${total} file${total > 1 ? 's' : ''} uploaded.`);
      } else if (succeeded === 0) {
        showToast.warning(
          `Collection attempt saved, but all ${total} attachment${total > 1 ? 's' : ''} failed to upload. Retry from Attachments: ${failures.join(', ')}`
        );
      } else {
        showToast.warning(
          `Collection attempt saved. ${succeeded} uploaded, ${failed} failed: ${failures.join(', ')}`
        );
      }

      onCancel();
    } catch {
      // addAttempt surfaces its own toast on error
    }
  };

  const handleAddInvoice = () => {
    const newInvoice: AttemptInvoiceDetail = { id: uniqueId('InvoiceId#') };
    setFormInvoiceValues([newInvoice, ...formInvoiceValues]);
  };

  const handleRemoveAttemptInvoice = (id: string) => {
    setFormInvoiceValues(formInvoiceValues.filter(inv => inv.id !== id));
  };

  const handleChangeInvoiceDetail = (id: string, invoiceDetail: AttemptInvoiceDetail) => {
    const updatedInvoices = formInvoiceValues.map(inv => (inv.id === id ? invoiceDetail : inv));
    setFormInvoiceValues(updatedInvoices);

    const isValidAllInvoices = every(
      updatedInvoices,
      (invoice: AttemptInvoiceDetail) => !!invoice.id
    );

    if (isValidAllInvoices) {
      setErrors({ ...errors, invoiceId: undefined });
    }
  };

  const isAllowedAddInvoice = useMemo(
    () => every(formInvoiceValues, (invoice: AttemptInvoiceDetail) => !!invoice.invoiceIds?.length),
    [formInvoiceValues]
  );

  const isBusy = addAttempt.isPending || isUploadingAttachments;

  return (
    <div id="attempts-form">
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-4">
          <DatePicker
            mode="single"
            required
            label="Date"
            placeholder={dateUtil.DateFormat.US}
            value={formValues.attemptDateTime ? new Date(formValues.attemptDateTime) : undefined}
            error={errors.attemptDateTime}
            onChange={attemptDateTime =>
              updateFormValues({
                attemptDateTime: attemptDateTime
                  ? dateUtil.formatDate(attemptDateTime, dateUtil.DateFormat.US)
                  : '',
              })
            }
          />

          <Select
            label="Contact Method"
            required
            error={errors.contactMethod}
            placeholder="Select method"
            options={CONTACT_METHOD_OPTIONS}
            value={formValues.contactMethod}
            onChange={option => {
              if (typeof option === 'string') {
                updateFormValues({ contactMethod: option });
                return;
              }
              updateFormValues({ contactMethod: option?.value as ContactMethod });
            }}
            fullWidth
          />
        </div>

        <div className="grid grid-cols-1 mt-4">
          <Textarea
            label="Notes"
            value={formValues.notesGlobal}
            onChange={e => updateFormValues({ notesGlobal: e.target.value })}
            placeholder="Enter details of the collection attempt..."
            required
            error={errors.notesGlobal}
            className={`${errors.notesGlobal ? 'border border-red-400' : ''}`}
            fullWidth
            resize="vertical"
            minRows={4}
            maxRows={8}
          />
        </div>

        <div>
          <h6 className="mt-8 border-t dark:border-xtnd-dark py-4 flex items-center gap-4">
            Invoices{' '}
            <Button onClick={handleAddInvoice} disabled={!isAllowedAddInvoice}>
              <Icon name="FaPlus" size="sm" />
            </Button>
          </h6>

          <div className="max-h-80 overflow-y-auto flex flex-col gap-4">
            {formInvoiceValues.map(invoiceDetail => (
              <div key={`${invoiceDetail.id}`}>
                <AttemptInvoiceForm
                  matterInvoiceOptions={matterInvoiceOptions}
                  error={errors.invoiceId}
                  invoiceDetail={invoiceDetail}
                  onChangeInvoiceDetail={invoice =>
                    handleChangeInvoiceDetail(invoiceDetail.id, invoice)
                  }
                  onRemove={handleRemoveAttemptInvoice}
                />
              </div>
            ))}
          </div>
        </div>

        <Card className="p-6 shadow-sm mt-8">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-gray-100 dark:bg-xtnd-dark-700 rounded-lg flex items-center justify-center mr-4">
              <Icon
                name="HiOutlinePaperClip"
                className="w-5 h-5 text-xtnd-dark-600 dark:text-xtnd-light-400"
              />
            </div>
            <div>
              <h2 className="text-xl font-bold text-xtnd-dark dark:text-xtnd-light">
                Attachments
              </h2>
            </div>
          </div>

          <div className="mt-4 space-y-4">
            <label className="flex flex-col text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
              <p>Upload new files</p>
            </label>
            <FileUpload
              isMultiple
              accept=".eml,.doc,.docx,.pdf"
              maxSize={20 * 1024 * 1024}
              maxFileNumber={12}
              files={stagedFiles}
              onChange={setStagedFiles}
              isDisplayUploadButton={false}
            />
          </div>
        </Card>

        <div className="mt-8 border-t dark:border-xtnd-dark py-4">
          <div className="date-picker-container relative">
            <DatePicker
              mode="single"
              label="Next Follow-up Date (Optional)"
              placeholder="Pick a date"
              min={new Date()}
              error={errors.followUp}
              value={formValues.nextFollowUp ? new Date(formValues.nextFollowUp) : undefined}
              onChange={nextFollowUp =>
                updateFormValues({
                  nextFollowUp: nextFollowUp
                    ? dateUtil.formatDate(nextFollowUp, dateUtil.DateFormat.US)
                    : '',
                })
              }
            />
            <style
              dangerouslySetInnerHTML={{
                __html: `
                    .date-picker-container div > div.absolute {
                    bottom: 3rem;
                    }
                  `,
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4">
          <Button
            variant="primary"
            className="col-span-2"
            type="submit"
            disabled={isBusy}
            loading={isBusy}
          >
            Save Collection Attempt
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

type AttemptInvoiceProps = {
  invoiceDetail: AttemptInvoiceDetail;
  matterInvoiceOptions: { value: string; label: string }[];
  error?: string;
  onChangeInvoiceDetail: (value: AttemptInvoiceDetail) => void;
  onRemove: (id: string) => void;
};

const AttemptInvoiceForm = ({
  invoiceDetail,
  error,
  matterInvoiceOptions,
  onChangeInvoiceDetail,
  onRemove,
}: AttemptInvoiceProps) => {
  const update = useCallback(
    (patch: Partial<any>) => {
      onChangeInvoiceDetail({ ...invoiceDetail, ...patch });
    },
    [invoiceDetail, onChangeInvoiceDetail]
  );

  return (
    <div className="max-h-80 overflow-y-auto flex flex-col gap-4">
      <Card key={`#${invoiceDetail.id}-detail`}>
        <CardHeader>
          <Select
            label="Invoice #"
            placeholder="Select invoice"
            options={matterInvoiceOptions}
            error={!invoiceDetail.invoiceIds?.length ? error : undefined}
            value={matterInvoiceOptions.filter(o => invoiceDetail.invoiceIds?.includes(o.value))}
            required
            multiple
            onChange={options => {
              if (Array.isArray(options)) {
                const invoiceIds = options.map(o => o.value);
                update({ invoiceIds });
              }
            }}
            fullWidth
          />
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-3 gap-6">
            <Select
              label="Payment Outcome"
              placeholder="Select outcome"
              options={PAYMENT_OUTCOME_OPTIONS}
              value={invoiceDetail.paymentOutcome}
              onChange={option => {
                if (typeof option === 'string') {
                  update({ paymentOutcome: option });
                  return;
                }
                update({ paymentOutcome: option?.value });
              }}
              fullWidth
              clearable
            />
            <Select
              label="Contact Attempt"
              placeholder="Select result"
              options={CONTACT_RESULT_OPTIONS}
              value={invoiceDetail.contactAttempt}
              onChange={option => {
                if (typeof option === 'string') {
                  update({ contactAttempt: option });
                  return;
                }
                update({ contactAttempt: option?.value });
              }}
              fullWidth
              clearable
            />
            <Select
              label="Administrative"
              placeholder="Select action"
              options={ADMINISTRATIVE_ACTION_OPTIONS}
              value={invoiceDetail.administrativeFlag}
              onChange={option => {
                if (typeof option === 'string') {
                  update({ administrativeFlag: option });
                  return;
                }
                update({ administrativeFlag: option?.value });
              }}
              fullWidth
              clearable
            />
          </div>
        </CardBody>
        <CardFooter className="border-none flex justify-end">
          <Button variant="outline" onClick={() => onRemove(invoiceDetail.id)}>
            Remove
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default CollectionAttemptForm;
